import React, { useState, useEffect, useMemo } from 'react';
import {
    Smartphone,
    User,
    X,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    ScanBarcode,
    MapPin,
    Download,
    CheckCircle2,
    LogOut,
    Wrench,
    Tablet,
    Boxes,
} from 'lucide-react';
import AdminPageHeader from '../components/admin-page-header';
import FilterPills from '../components/admin/FilterPills';
import KpiCard from '../components/admin/KpiCard';
import { toast } from 'react-toastify';
import '../styles/devices.css';
import '../styles/add-user.css';
import { useTranslation } from '../i18n';
import { apiFetch } from "../api/apiFetch";
import { canonicalWarehouse } from '../components/helpers/warehouseAlias';
import { useReadOnly } from '../access/ReadOnly';
import { useWarehouseScope } from '../access/useWarehouseScope';

// Tiny CSV serialiser — quotes any cell that contains commas, quotes,
// or newlines and doubles up embedded quotes per RFC 4180. Mirrors the
// helper used in TotesAdmin / ProductsAdmin so exports look identical
// across the admin section.
const UTF8_BOM = '﻿';

const csvEscape = (v: unknown): string => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const triggerDownload = (filename: string, contents: string) => {
    const blob = new Blob([UTF8_BOM + contents], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

interface Device {
    DeviceRFID: number;
    // Plain string (not a hardcoded union) — warehouses are dynamic
    // (admin Settings can add / rename them) and ~87 legacy records
    // still carry "Pembroke Dock" from before the rename. The
    // canonicalWarehouse() helper folds those aliases when filtering.
    Warehouse: string;
    DeviceType: 'scanner' | 'phone' | 'zebra';
    DeviceName: string;
    Status: 'Checked In' | 'Checked Out' | 'Maintenance';
    LastUpdated: string;
    LastUpdatedBy: string;
    Notes: string;
}

interface HistoryEntry {
    DateTime: string;
    Action: 'Checked In' | 'Checked Out' | 'Maintenance';
    Username: string;
    Warehouse: string;
}

type WarehouseType = string;
type DeviceTypeFilter = Device['DeviceType'] | '';
type StatusType = Device['Status'] | '';

// One sortable field per column header. The state pairs a field with a
// direction so column-click toggling and the dropdown stay coherent —
// the dropdown picks both at once, a header click sets the field and
// either flips direction (same column) or applies the field's default
// direction (different column).
type SortField = 'updated' | 'name' | 'type' | 'status' | 'warehouse' | 'updatedBy';
type SortDirection = 'asc' | 'desc';
interface SortState {
    field: SortField;
    direction: SortDirection;
}

// Default direction for a fresh click on each column. "Last updated"
// reads newest-first because that's what an admin almost always wants;
// alphabetic columns read A→Z first.
const FIELD_DEFAULT_DIR: Record<SortField, SortDirection> = {
    updated: 'desc',
    name: 'asc',
    type: 'asc',
    status: 'asc',
    warehouse: 'asc',
    updatedBy: 'asc',
};

const sortKey = (s: SortState) => `${s.field}_${s.direction}`;

// The dropdown only surfaces the most common sort directions per
// column — every column is also clickable in the table, where the
// user can flip direction. Sorts that don't appear here (Type, Updated
// By, status_desc, warehouse_desc) leave the dropdown blank.
const DROPDOWN_KEYS: ReadonlySet<string> = new Set([
    'updated_desc',
    'updated_asc',
    'name_asc',
    'name_desc',
    'status_asc',
    'warehouse_asc',
]);

const parseSortKey = (key: string): SortState | null => {
    const idx = key.lastIndexOf('_');
    if (idx < 0) return null;
    const field = key.slice(0, idx) as SortField;
    const direction = key.slice(idx + 1) as SortDirection;
    if (!(field in FIELD_DEFAULT_DIR)) return null;
    if (direction !== 'asc' && direction !== 'desc') return null;
    return { field, direction };
};

const PAGE_SIZE = 25;

// Read the signed-in user's warehouse so the table opens scoped to
// their site — operators rarely need to see other warehouses' devices.
const readSignedInWarehouse = (): WarehouseType => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return "";
        const u = JSON.parse(raw);
        const w = typeof u?.warehouse === "string" ? u.warehouse : "";
        if (w === "Newport" || w === "Poland" || w === "Pembroke") return w;
        return "";
    } catch { return ""; }
};

const AdminDevices = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [devices, setDevices] = useState<Device[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState<WarehouseType>(readSignedInWarehouse);
    // Without cross_warehouse the table is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [deviceTypeFilter, setDeviceTypeFilter] = useState<DeviceTypeFilter>('');
    const [statusFilter, setStatusFilter] = useState<StatusType>('');
    const [sort, setSort] = useState<SortState>({ field: 'updated', direction: 'desc' });
    const [page, setPage] = useState(0);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [showDeviceModal, setShowDeviceModal] = useState<boolean>(false);
    const [deviceNotes, setDeviceNotes] = useState<string>('');
    const [, setNotesSaved] = useState<boolean>(false);
    const [isNotesLoading, setIsNotesLoading] = useState<boolean>(false);

    const [deviceHistory, setDeviceHistory] = useState<Record<string, HistoryEntry[]>>({});

    useEffect(() => { setPage(0); }, [searchTerm, warehouseFilter, deviceTypeFilter, statusFilter, sort]);

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const response = await apiFetch(`/devices/all`, {});
                if (!response.ok) throw new Error('Network response was not ok');
                const data: Device[] = await response.json();
                setDevices(data);
            } catch (error) {
                console.error('Error fetching devices:', error);
            }
        };

        const fetchDeviceHistory = async () => {
            try {
                const response = await apiFetch(`/devices/history`, {});
                if (!response.ok) throw new Error('Network response was not ok');
                const data: Record<string, HistoryEntry[]> = await response.json();
                setDeviceHistory(data);
            } catch (error) {
                console.error('Error fetching device history:', error);
            }
        };

        fetchDevices();
        fetchDeviceHistory();
    }, []);

    // Warehouse counts feed the FilterPills row — built from all devices
    // so the badges stay stable as the operator clicks between sites.
    // Counts are aggregated by *canonical* warehouse name so legacy
    // values (e.g. "Pembroke Dock") fold into the renamed warehouse
    // ("Pembroke") rather than appearing as a phantom site.
    const warehouseCounts = useMemo(() => {
        const out: Record<string, number> = {};
        for (const d of devices) {
            const canon = canonicalWarehouse(d.Warehouse);
            if (canon) out[canon] = (out[canon] ?? 0) + 1;
        }
        return out;
    }, [devices]);

    const warehouses = useMemo(
        () => Object.keys(warehouseCounts).sort().filter(w =>
            warehouseScope.canCross || canonicalWarehouse(w) === canonicalWarehouse(warehouseScope.own)),
        [warehouseCounts, warehouseScope.canCross, warehouseScope.own],
    );

    const filteredDevices = useMemo<Device[]>(() => {
        const q = searchTerm.toLowerCase();
        const filtered = devices.filter(device => {
            const matchesSearch = !q ||
                device.DeviceName.toLowerCase().includes(q) ||
                device.LastUpdatedBy.toLowerCase().includes(q);
            // Compare against the *canonical* warehouse so a record
            // tagged "Pembroke Dock" matches a "Pembroke" filter pill.
            const matchesWarehouse = !warehouseFilter
                || canonicalWarehouse(device.Warehouse) === canonicalWarehouse(warehouseFilter);
            const matchesDeviceType = !deviceTypeFilter || device.DeviceType === deviceTypeFilter;
            const matchesStatus = !statusFilter || device.Status === statusFilter;

            return matchesSearch && matchesWarehouse && matchesDeviceType && matchesStatus;
        });

        // Parse `LastUpdated` once per row — it's an ISO-ish string from
        // the API. NaN-safe: if the parse fails the row sinks to the
        // bottom on date sorts, which is more useful than throwing.
        const ts = (d: Device) => {
            const n = Date.parse(d.LastUpdated);
            return Number.isFinite(n) ? n : 0;
        };
        // Stable status order (most operationally meaningful first) so
        // ascending status puts Maintenance + Checked Out — the rows
        // an admin actually needs to act on — above Checked In.
        const statusRank = (s: Device['Status']) =>
            s === 'Maintenance' ? 0 : s === 'Checked Out' ? 1 : 2;

        const sign = sort.direction === 'asc' ? 1 : -1;
        const sorted = [...filtered];
        switch (sort.field) {
            case 'updated':
                sorted.sort((a, b) => sign * (ts(a) - ts(b)));
                break;
            case 'name':
                sorted.sort((a, b) => sign * a.DeviceName.localeCompare(b.DeviceName));
                break;
            case 'type':
                sorted.sort((a, b) =>
                    sign * a.DeviceType.localeCompare(b.DeviceType) ||
                    a.DeviceName.localeCompare(b.DeviceName));
                break;
            case 'status':
                sorted.sort((a, b) =>
                    sign * (statusRank(a.Status) - statusRank(b.Status)) ||
                    (ts(b) - ts(a)));
                break;
            case 'warehouse':
                sorted.sort((a, b) =>
                    sign * a.Warehouse.localeCompare(b.Warehouse) ||
                    a.DeviceName.localeCompare(b.DeviceName));
                break;
            case 'updatedBy':
                sorted.sort((a, b) =>
                    sign * (a.LastUpdatedBy || '').localeCompare(b.LastUpdatedBy || '') ||
                    a.DeviceName.localeCompare(b.DeviceName));
                break;
        }
        return sorted;
    }, [devices, searchTerm, warehouseFilter, deviceTypeFilter, statusFilter, sort]);

    // Summary strip stats are always over the warehouse-scoped set so
    // the totals reflect what the operator's looking at, not the entire
    // multi-warehouse fleet.
    const summaryDevices = useMemo(
        () => warehouseFilter
            ? devices.filter(d => canonicalWarehouse(d.Warehouse) === canonicalWarehouse(warehouseFilter))
            : devices,
        [devices, warehouseFilter],
    );

    const stats = useMemo(() => {
        const checkedIn = summaryDevices.filter(d => d.Status === 'Checked In').length;
        const checkedOut = summaryDevices.filter(d => d.Status === 'Checked Out').length;
        const maintenance = summaryDevices.filter(d => d.Status === 'Maintenance').length;
        const scanners = summaryDevices.filter(d => d.DeviceType === 'scanner').length;
        const handhelds = summaryDevices.filter(d => d.DeviceType === 'phone' || d.DeviceType === 'zebra').length;
        return { checkedIn, checkedOut, maintenance, scanners, handhelds };
    }, [summaryDevices]);

    const totalPages = Math.max(1, Math.ceil(filteredDevices.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const sliceStart = safePage * PAGE_SIZE;
    const sliceEnd = sliceStart + PAGE_SIZE;
    const visible = filteredDevices.slice(sliceStart, sliceEnd);
    const rangeFrom = filteredDevices.length === 0 ? 0 : sliceStart + 1;
    const rangeTo = Math.min(sliceEnd, filteredDevices.length);

    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleDeviceClick = (device: Device): void => {
        setSelectedDevice(device);
        setDeviceNotes(device.Notes);
        setNotesSaved(true);
        setShowDeviceModal(true);
    };

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDeviceNotes(e.target.value);
        setNotesSaved(false);
    };

    const closeDeviceModal = (): void => {
        setShowDeviceModal(false);
        setSelectedDevice(null);
        setNotesSaved(true);
        setDeviceNotes('');
    };

    const updateDeviceNotes = async (): Promise<void> => {
        if (!selectedDevice) return;

        setIsNotesLoading(true);

        try {
            const response = await apiFetch(`/devices/notes`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    deviceName: selectedDevice.DeviceName,
                    notes: deviceNotes,
                }),
            });

            if (response.ok) {
                setDevices(prev =>
                    prev.map(d =>
                        d.DeviceName === selectedDevice.DeviceName
                            ? { ...d, Notes: deviceNotes }
                            : d
                    )
                );
                setSelectedDevice(prev => prev ? { ...prev, Notes: deviceNotes } : null);
                setNotesSaved(true);
                setTimeout(() => setNotesSaved(false), 3000);
                toast.success(t("success"));
            } else {
                throw new Error('Failed to update notes');
            }
        } catch (error) {
            console.error('Error updating device notes:', error);
            toast.error(t("server_error"));
        } finally {
            setIsNotesLoading(false);
        }
    };

    const getStatusClass = (status: Device['Status']): string => {
        switch (status) {
            case 'Checked In': return 'devices-status devices-status--in';
            case 'Checked Out': return 'devices-status devices-status--out';
            case 'Maintenance': return 'devices-status devices-status--maintenance';
            default: return 'devices-status';
        }
    };

    const getHistoryStatusClass = (action: HistoryEntry['Action']): string => {
        switch (action) {
            case 'Checked In': return 'history-checked-in';
            case 'Checked Out': return 'history-checked-out';
            case 'Maintenance': return 'history-maintenance';
            default: return 'history-default';
        }
    };

    const renderDeviceIcon = (type: Device['DeviceType']) =>
        type === 'scanner'
            ? <ScanBarcode size={16} style={{ color: 'var(--color-text-muted, #6b7280)' }} />
            : <Smartphone size={16} style={{ color: 'var(--color-text-muted, #6b7280)' }} />;

    // Click a column header — same column flips direction, different
    // column resets to that field's natural default direction.
    const onColumnSort = (field: SortField) => {
        setSort(prev => prev.field === field
            ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { field, direction: FIELD_DEFAULT_DIR[field] });
    };

    // Reusable sortable <th>. Renders the label, an arrow that reflects
    // the active direction (or a faint up/down stack when inactive), and
    // wires the whole cell up as a button for keyboard activation.
    const SortableTh = ({
        field,
        label,
        width,
    }: { field: SortField; label: string; width?: number }) => {
        const active = sort.field === field;
        const Icon = !active ? ChevronsUpDown : sort.direction === 'asc' ? ChevronUp : ChevronDown;
        const ariaSort = !active ? 'none' : sort.direction === 'asc' ? 'ascending' : 'descending';
        return (
            <th
                style={width ? { width } : undefined}
                aria-sort={ariaSort}
                className={`devices-th-sort${active ? ' devices-th-sort--active' : ''}`}
            >
                <button
                    type="button"
                    className="devices-th-sort-btn"
                    onClick={() => onColumnSort(field)}
                >
                    <span>{label}</span>
                    <Icon size={12} className="devices-th-sort-icon" aria-hidden />
                </button>
            </th>
        );
    };

    // Exports the *currently filtered* set so the file matches what the
    // admin sees on screen — narrowing the table by warehouse / status
    // also narrows the export, which is almost always what's wanted.
    const handleExport = () => {
        if (filteredDevices.length === 0) {
            toast.info(t("no_devices"));
            return;
        }
        const headers = [
            t("device"),
            t("warehouse"),
            t("type"),
            t("status"),
            t("last_updated"),
            t("updated_by"),
            t("notes"),
        ];
        const rows = filteredDevices.map(d => [
            d.DeviceName,
            d.Warehouse,
            d.DeviceType,
            d.Status,
            d.LastUpdated,
            d.LastUpdatedBy,
            d.Notes,
        ]);
        const csv = [headers, ...rows]
            .map(row => row.map(csvEscape).join(','))
            .join('\r\n');
        const stamp = new Date().toISOString().slice(0, 10);
        triggerDownload(`devices-${stamp}.csv`, csv);
    };

    return (
        <div className="page-data page-data-wide">
            <AdminPageHeader
                titleKey="device_management"
                backTo="/admin/hardware"
                hideSearch
                crumbs={[
                    { labelKey: "admin_panel", to: "/admin" },
                    { labelKey: "hardware", to: "/admin/hardware" },
                    { labelKey: "devices" },
                ]}
            />

            <div className="users-admin">
                {/* Six-card KPI strip — totals + status counts + device-type
                    counts. Always reflects the warehouse pill scope so the
                    numbers stay coherent with the table below. */}
                <div className="devices-kpi-grid">
                    <KpiCard
                        labelKey="total_devices"
                        state="ok"
                        value={summaryDevices.length}
                        icon={<Boxes size={16} />}
                    />
                    <KpiCard
                        labelKey="checked_in"
                        state="ok"
                        value={stats.checkedIn}
                        icon={<CheckCircle2 size={16} />}
                    />
                    <KpiCard
                        labelKey="checked_out"
                        state="ok"
                        value={stats.checkedOut}
                        icon={<LogOut size={16} />}
                    />
                    <KpiCard
                        labelKey="maintenance"
                        state="ok"
                        value={stats.maintenance}
                        icon={<Wrench size={16} />}
                    />
                    <KpiCard
                        labelKey="scanners"
                        state="ok"
                        value={stats.scanners}
                        icon={<ScanBarcode size={16} />}
                    />
                    <KpiCard
                        labelKey="handhelds"
                        state="ok"
                        value={stats.handhelds}
                        icon={<Tablet size={16} />}
                    />
                </div>

                <FilterPills
                    value={warehouseFilter}
                    onChange={v => setWarehouseFilter(v as WarehouseType)}
                    allCount={devices.length}
                    hideAll={!warehouseScope.canCross}
                    options={warehouses.map(w => ({ value: w, label: w, count: warehouseCounts[w] }))}
                />

                {/* Single-row filter bar: search · type · status · Export.
                    The Export button is anchored to the right via
                    margin-left:auto on the .devices-filters-export wrapper
                    so it sits flush with the right edge of the table. */}
                <div className="locations-table-filters devices-filters-row">
                    <input
                        type="search"
                        className="locations-table-search"
                        placeholder={t("search_devices")}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="locations-table-input devices-filter-select"
                        value={deviceTypeFilter}
                        onChange={e => setDeviceTypeFilter(e.target.value as DeviceTypeFilter)}
                    >
                        <option value="">{t("all_types")}</option>
                        <option value="scanner">{t("scanners")}</option>
                        <option value="phone">{t("phones")}</option>
                        <option value="zebra">{t("zebras")}</option>
                    </select>
                    <select
                        className="locations-table-input devices-filter-select"
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value as StatusType)}
                    >
                        <option value="">{t("all_statuses")}</option>
                        <option value="Checked In">{t("checked_in")}</option>
                        <option value="Checked Out">{t("checked_out")}</option>
                        <option value="Maintenance">{t("maintenance")}</option>
                    </select>
                    <select
                        className="locations-table-input devices-filter-select devices-sort-select"
                        value={DROPDOWN_KEYS.has(sortKey(sort)) ? sortKey(sort) : ''}
                        onChange={e => {
                            const next = parseSortKey(e.target.value);
                            if (next) setSort(next);
                        }}
                        aria-label={t("sort_by")}
                    >
                        {/* Hidden placeholder — shown when the active sort
                            (e.g. clicked Type column) doesn't map to one of
                            the six visible options. */}
                        <option value="" hidden>—</option>
                        <option value="updated_desc">{t("sort_updated_desc")}</option>
                        <option value="updated_asc">{t("sort_updated_asc")}</option>
                        <option value="name_asc">{t("sort_name_asc")}</option>
                        <option value="name_desc">{t("sort_name_desc")}</option>
                        <option value="status_asc">{t("sort_status")}</option>
                        <option value="warehouse_asc">{t("sort_warehouse")}</option>
                    </select>
                    <button
                        type="button"
                        className="devices-export-btn"
                        onClick={handleExport}
                    >
                        <Download size={14} />
                        <span>{t("export")}</span>
                    </button>
                </div>

                {filteredDevices.length === 0 ? (
                    <div className="locations-table-empty">
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("no_devices")}</div>
                        <div style={{ fontSize: "0.82rem" }}>{t("adjust_filters")}</div>
                    </div>
                ) : (
                    <>
                        <div className="locations-table-wrap">
                            <table className="locations-table">
                                <thead>
                                    <tr>
                                        <SortableTh field="name" label={t("device")} />
                                        <SortableTh field="warehouse" label={t("warehouse")} width={140} />
                                        <SortableTh field="type" label={t("type")} width={110} />
                                        <SortableTh field="status" label={t("status")} width={130} />
                                        <SortableTh field="updated" label={t("last_updated")} width={170} />
                                        <SortableTh field="updatedBy" label={t("updated_by")} width={140} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map(device => (
                                        <tr
                                            key={device.DeviceRFID}
                                            className="devices-row"
                                            onClick={() => handleDeviceClick(device)}
                                        >
                                            <td>
                                                <div className="devices-cell-name">
                                                    {renderDeviceIcon(device.DeviceType)}
                                                    <span style={{ fontWeight: 600 }}>{device.DeviceName}</span>
                                                </div>
                                            </td>
                                            <td>{device.Warehouse}</td>
                                            <td style={{ textTransform: "capitalize" }}>{device.DeviceType}</td>
                                            <td>
                                                <span className={getStatusClass(device.Status)}>
                                                    {device.Status}
                                                </span>
                                            </td>
                                            <td style={{ color: "var(--color-text-muted, #6b7280)", fontSize: "0.82rem" }}>
                                                {formatDateTime(device.LastUpdated)}
                                            </td>
                                            <td>{device.LastUpdatedBy || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="locations-table-footer">
                            <span>{t("locations_showing_range", {
                                from: String(rangeFrom),
                                to: String(rangeTo),
                                total: String(filteredDevices.length),
                            })}</span>
                            <div className="locations-table-pagination">
                                <button
                                    type="button"
                                    className="locations-table-page-btn"
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={safePage === 0}
                                    aria-label={t("pagination_previous")}
                                >
                                    <ChevronLeft size={16} />
                                    <span>{t("pagination_previous")}</span>
                                </button>
                                <span className="locations-table-page-indicator">
                                    {t("pagination_page_x_of_y", {
                                        page: String(safePage + 1),
                                        total: String(totalPages),
                                    })}
                                </span>
                                <button
                                    type="button"
                                    className="locations-table-page-btn"
                                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={safePage >= totalPages - 1}
                                    aria-label={t("pagination_next")}
                                >
                                    <span>{t("pagination_next")}</span>
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {showDeviceModal && selectedDevice && (
                    <div className="modal-overlay">
                        <div className="modal-container">
                            <div className="modal-header">
                                <div className="modal-title-section">
                                    {selectedDevice.DeviceType === 'scanner' ? (
                                        <ScanBarcode className="modal-icon" />
                                    ) : (
                                        <Smartphone className="modal-icon" />
                                    )}
                                    <div>
                                        <h3 className="modal-title">{selectedDevice.DeviceName}</h3>
                                        <p className="modal-subtitle">{selectedDevice.DeviceType} • {selectedDevice.Warehouse}</p>
                                    </div>
                                </div>
                                <button onClick={closeDeviceModal} className="modal-close-btn">
                                    <X className="icon-md" />
                                </button>
                            </div>

                            <div className="modal-content">
                                <div className="device-details-grid">
                                    <div>
                                        <label className="detail-label">{t("current_status")}</label>
                                        <div className="detail-value">
                                            <span className={getStatusClass(selectedDevice.Status)}>
                                                {selectedDevice.Status}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="detail-label">{t("last_updated")}</label>
                                        <p className="detail-text">{formatDateTime(selectedDevice.LastUpdated)}</p>
                                    </div>
                                    <div>
                                        <label className="detail-label">{t("updated_by")}</label>
                                        <p className="detail-text">{selectedDevice.LastUpdatedBy}</p>
                                    </div>
                                    <div>
                                        <label className="detail-label">{t("warehouse")}</label>
                                        <p className="detail-text">{selectedDevice.Warehouse}</p>
                                    </div>
                                    <div className='details-grid-span-2'>
                                        <label className='detail-label'>{t("notes")}</label>
                                        <textarea
                                            className='detail-textarea'
                                            value={deviceNotes}
                                            onChange={handleNotesChange}
                                            placeholder={t("no_notes")}
                                        />
                                        <button
                                            onClick={updateDeviceNotes}
                                            className={`notes-save-btn ${isNotesLoading ? 'loading' : ''}`}
                                            disabled={isNotesLoading || readOnly}
                                            aria-disabled={readOnly}
                                            title={readOnly ? t("read_only_mode") : undefined}
                                        >
                                            {isNotesLoading ? t("saving") : t("save_notes")}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="history-title">{t("usage_history")}</h4>
                                    <div className="history-container">
                                        <div className="history-list">
                                            {deviceHistory[selectedDevice.DeviceName]?.map((entry: HistoryEntry, index: number) => (
                                                <div key={index} className="history-item">
                                                    <div className={`history-dot ${getHistoryStatusClass(entry.Action)}`}></div>
                                                    <div className="history-content">
                                                        <div className="history-header">
                                                            <p className="history-action">{entry.Action}</p>
                                                            <p className="history-date">{formatDateTime(entry.DateTime)}</p>
                                                        </div>
                                                        <div className="history-details">
                                                            <span className="history-detail">
                                                                <User className="history-detail-icon" />
                                                                {entry.Username}
                                                            </span>
                                                            <span className="history-detail">
                                                                <MapPin className="history-detail-icon" />
                                                                {entry.Warehouse}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )) || (
                                                <p className="history-empty">{t("no_history")}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button onClick={closeDeviceModal} className="modal-close-action">
                                    {t("close")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AdminDevices;
